"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Droplets, Gauge, Volume2, CircleAlert, TrendingUp, Waves, PhoneCall, ShieldAlert, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
const warningItems = [{
  id: "low-pressure",
  icon: Gauge,
  title: "Sudden Drop in Water Pressure",
  badge: "Immediate Risk",
  description: "A sudden or persistent drop in faucet or shower pressure often signals a hidden pipe rupture, severe mineral buildup, or an underground line breach."
}, {
  id: "discolored-water",
  icon: Droplets,
  title: "Rust-Colored or Cloudy Water",
  badge: "Pipe Corrosion",
  description: "Brown, yellow, or murky water indicates internal pipe corrosion, rust detachment, or sediment buildup that compromises your entire water supply."
}, {
  id: "knocking-sounds",
  icon: Volume2,
  title: "Banging or Water Hammer Sounds",
  badge: "Pressure Imbalance",
  description: "Loud thumping, rattling, or whistling behind drywall indicates loose pipe mountings, trapped air pockets, or destructive pressure waves."
}, {
  id: "damp-spots",
  icon: Waves,
  title: "Wall Stains & Unexplained Moisture",
  badge: "Hidden Leaks",
  description: "Soggy drywall, warped baseboards, mold growth, or warm spots under flooring indicate active slab leaks or pinhole line cracks."
}, {
  id: "spike-bills",
  icon: TrendingUp,
  title: "Unexplained Surge in Water Bills",
  badge: "Continuous Loss",
  description: "If your monthly utility charges jump without increased household usage, hundreds of gallons may be leaking beneath your foundation."
}, {
  id: "slow-drains",
  icon: CircleAlert,
  title: "Recurring Slow Drains & Gurgling",
  badge: "Sewer Line Issue",
  description: "Multiple fixtures draining sluggishly with bubbling or foul sewage odors points to severe mainline blockages or tree root intrusion."
}];
export default function WarningSigns() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: shouldReduceMotion ? 0 : custom * 0.08,
        ease: "easeOut"
      }
    })
  };
  return <section id="pipewarningsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 border-accent text-accent-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Critical Diagnostics
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Warning Signs Your Pipes Need Professional Repair
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Plumbing failures rarely happen without warning. Catching pipe deterioration early prevents catastrophic water damage, mold growth, and emergency restoration costs in Jonesboro.
          </p>
        </div>

        {/* Warning Signs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {warningItems.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="size-6 shrink-0" aria-hidden="true" />
                      </div>
                      <Badge variant="secondary" className="text-2xs font-semibold px-2 py-0.5">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 pb-6">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Emergency Alert Banner */}
        <div className="mt-8">
          <Alert className="border-destructive/30 bg-card p-6 rounded-xl shadow-sm">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 w-full">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-md bg-destructive/10 text-destructive mt-0.5 shrink-0">
                  <ShieldAlert className="size-6" aria-hidden="true" />
                </div>
                <div>
                  <AlertTitle className="text-lg font-bold text-foreground">
                    Experiencing Active Flooding or a Burst Pipe Right Now?
                  </AlertTitle>
                  <AlertDescription className="text-muted-foreground text-sm mt-1">
                    Shut off your main water valve immediately and contact our Jonesboro 24/7 rapid dispatch unit.
                  </AlertDescription>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                <Button asChild variant="destructive" size="lg" className="w-full sm:w-auto font-semibold">
                  <a href="tel:4708479076">
                    <PhoneCall className="size-4 mr-1" />
                    Call Emergency Plumber
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/contact/">
                    Schedule Pipe Inspection
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      </div>
    </section>;
}